import { Product, CreateProductData, UpdateProductData, CreateOrderData, Order } from './types';
import { config } from './config';

const API_BASE = import.meta.env.VITE_API_BASE || config.api.baseUrl;

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function apiRequest<T>(endpoint: string, options: RequestInit & { skipContentType?: boolean } = {}): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  
  const config: RequestInit = {
    ...options,
  };

  // Get access token from localStorage
  const authData = localStorage.getItem('simplepos-auth');
  let accessToken = null;
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      accessToken = parsed.accessToken;
    } catch (error) {
      console.error('Error parsing auth data:', error);
    }
  }

  // Only set Content-Type if not skipping it (for FormData)
  if (!options.skipContentType) {
    config.headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
  } else {
    // For FormData, only use headers if explicitly provided
    if (options.headers) {
      config.headers = options.headers;
    }
    // Don't set any default headers for FormData
  }

  // Add Authorization header if we have an access token
  if (accessToken) {
    config.headers = {
      ...config.headers,
      'Authorization': `Bearer ${accessToken}`,
    };
  }

  try {
    const response = await fetch(url, config);
    
    // If we get a 401, try to refresh the token
    if (response.status === 401) {
      const authData = localStorage.getItem('simplepos-auth');
      if (authData) {
        try {
          const parsed = JSON.parse(authData);
          if (parsed.refreshToken) {
            // Try to refresh the token
            const refreshResponse = await fetch(`${API_BASE}/auth/refresh`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ refreshToken: parsed.refreshToken }),
            });
            
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              if (refreshData.success && refreshData.accessToken) {
                // Update the access token
                localStorage.setItem('simplepos-auth', JSON.stringify({
                  ...parsed,
                  accessToken: refreshData.accessToken,
                  timestamp: Date.now()
                }));
                
                // Retry the original request with the new token
                config.headers = {
                  ...config.headers,
                  'Authorization': `Bearer ${refreshData.accessToken}`,
                };
                const retryResponse = await fetch(url, config);
                
                if (!retryResponse.ok) {
                  throw new ApiError(retryResponse.status, `HTTP error! status: ${retryResponse.status}`);
                }
                
                const text = await retryResponse.text();
                if (!text) {
                  return {} as T;
                }
                
                return JSON.parse(text);
              }
            }
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
        }
      }
    }
    
    if (!response.ok) {
      throw new ApiError(response.status, `HTTP error! status: ${response.status}`);
    }
    
    // Handle empty responses
    const text = await response.text();
    if (!text) {
      return {} as T;
    }
    
    return JSON.parse(text);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(0, `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export const api = {
  // Authentication
  async login(username: string, password: string): Promise<{ success: boolean; user: any; accessToken?: string; refreshToken?: string; message: string }> {
    return apiRequest<{ success: boolean; user: any; accessToken?: string; refreshToken?: string; message: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },

  async refreshToken(refreshToken: string): Promise<{ success: boolean; accessToken?: string; message: string }> {
    return apiRequest<{ success: boolean; accessToken?: string; message: string }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  },

  async logout(refreshToken: string): Promise<{ success: boolean; message: string }> {
    return apiRequest<{ success: boolean; message: string }>('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  },

  async changePassword(username: string, currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return apiRequest<{ success: boolean; message: string }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ username, currentPassword, newPassword }),
    });
  },

  async createUser(username: string, password: string, email?: string, role?: string): Promise<{ success: boolean; message: string }> {
    return apiRequest<{ success: boolean; message: string }>('/auth/users', {
      method: 'POST',
      body: JSON.stringify({ username, password, email, role }),
    });
  },

  // Products
  async getProducts(): Promise<Product[]> {
    return apiRequest<Product[]>('/products');
  },

  async createProduct(data: CreateProductData): Promise<{ success: boolean; message: string }> {
    return apiRequest<{ success: boolean; message: string }>('/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateProduct(id: number, data: UpdateProductData): Promise<{ success: boolean; message: string }> {
    return apiRequest<{ success: boolean; message: string }>(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteProduct(id: number): Promise<{ success: boolean; message: string }> {
    return apiRequest<{ success: boolean; message: string }>(`/products/${id}`, {
      method: 'DELETE',
    });
  },

  // Orders
  async createOrder(data: CreateOrderData): Promise<{ success: boolean; order_id: string; message: string }> {
    return apiRequest<{ success: boolean; order_id: string; message: string }>('/order', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getOrders(): Promise<Order[]> {
    return apiRequest<Order[]>('/orders');
  },

  async updateOrder(id: number, data: { status: string }): Promise<{ success: boolean; message: string }> {
    return apiRequest<{ success: boolean; message: string }>(`/orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteOrder(id: number): Promise<{ success: boolean; message: string }> {
    return apiRequest<{ success: boolean; message: string }>(`/orders/${id}`, {
      method: 'DELETE',
    });
  },

  // Image Upload
  async uploadImage(file: File): Promise<{ success: boolean; message: string; filename: string; url: string }> {
    const formData = new FormData();
    formData.append('image', file);
    
    console.log('Uploading file:', file.name, file.size, file.type);
    console.log('FormData entries:', Array.from(formData.entries()));
    
    return apiRequest<{ success: boolean; message: string; filename: string; url: string }>('/upload/image', {
      method: 'POST',
      body: formData,
      skipContentType: true, // Skip setting Content-Type for FormData
    });
  },
};

// Offline fallback using localStorage
export const offlineStorage = {
  getProducts(): Product[] {
    try {
      const stored = localStorage.getItem('simplepos-products');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  },

  saveProducts(products: Product[]): void {
    try {
      localStorage.setItem('simplepos-products', JSON.stringify(products));
    } catch (error) {
      console.error('Failed to save products to localStorage:', error);
    }
  },

  getOrders(): Order[] {
    try {
      const stored = localStorage.getItem('simplepos-orders');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  },

  saveOrder(order: Order): void {
    try {
      const orders = this.getOrders();
      orders.unshift(order);
      localStorage.setItem('simplepos-orders', JSON.stringify(orders));
    } catch (error) {
      console.error('Failed to save order to localStorage:', error);
    }
  },
};
